import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQuery, createApiErrorTransformer } from '@/lib/api/baseQuery';

const LIVE_RISK_BASE = import.meta.env.VITE_LIVE_RISK_URL;

export const liveRiskApi = createApi({
  reducerPath: 'liveRiskApi',
  baseQuery: createBaseQuery(LIVE_RISK_BASE),
  tagTypes: ['LiveRisk'],
  endpoints: (builder) => ({
    getClientWiseEq: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        searchClient?: string;
        source?: string;
        sortColumn?: string;
        sortDirection?: 'ASC' | 'DESC';
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        searchClient = '',
        source = 'ap',
        sortColumn,
        sortDirection,
      }) => ({
        url: `live-risk/client-wise-eq`,
        method: 'GET',
        params: { pageNumber, pageSize, searchClient, source, sortColumn, sortDirection },
      }),
      // normalize response to return `data` object directly
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch client-wise EQ');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskClientWise', true),
    }),
    getScriptWiseEq: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        searchSymbol?: string;
        source?: string;
        sortColumn?: string;
        sortDirection?: 'ASC' | 'DESC';
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        searchSymbol = '',
        source = 'ap',
        sortColumn,
        sortDirection,
      }) => ({
        url: `live-risk/script-wise-eq`,
        method: 'GET',
        params: { pageNumber, pageSize, searchSymbol, source, sortColumn, sortDirection },
      }),
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch script-wise EQ');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskScriptWiseEQ', true),
    }),
    getScriptWiseFno: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        source?: string;
        searchSymbol?: string;
        sortColumn?: string;
        sortDirection?: 'ASC' | 'DESC';
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        source = 'ap',
        searchSymbol = '',
        sortColumn,
        sortDirection,
      }) => ({
        url: `live-risk/script-wise-fno`,
        method: 'GET',
        params: { pageNumber, pageSize, source, searchSymbol, sortColumn, sortDirection },
      }),
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch script-wise FNO');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskScriptWiseFNO', true),
    }),
    getClientWiseFno: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        searchClient?: string;
        source?: string;
        sortColumn?: string;
        sortDirection?: 'ASC' | 'DESC';
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        searchClient = '',
        source = 'ap',
        sortColumn,
        sortDirection,
      }) => ({
        url: `live-risk/client-wise-fno`,
        method: 'GET',
        params: { pageNumber, pageSize, searchClient, source, sortColumn, sortDirection },
      }),
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch client-wise FNO');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskClientWiseFNO', true),
    }),
    getFNORiskTransactionDetailsService: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        searchClient?: string;
        searchSymbol?: string;
        source?: string;
        sortColumn?: string;
        sortDirection?: 'ASC' | 'DESC';
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        searchClient = '',
        searchSymbol = '',
        source = 'ap',
        sortColumn,
        sortDirection,
      }) => ({
        url: `live-risk/fno-transaction-details`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          searchClient,
          searchSymbol,
          source,
          sortColumn,
          sortDirection,
        },
      }),
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch FNO risk transaction details');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskFNORiskTransactionDetails', true),
    }),
    getEQRiskTransactionDetailsService: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        searchClient?: string;
        searchSymbol?: string;
        source?: string;
        sortColumn?: string;
        sortDirection?: 'ASC' | 'DESC';
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        searchClient = '',
        searchSymbol = '',
        source = 'ap',
        sortColumn,
        sortDirection,
      }) => ({
        url: `live-risk/eq-transaction-details`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          searchClient,
          searchSymbol,
          source,
          sortColumn,
          sortDirection,
        },
      }),
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch EQ risk transaction details');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskEQRiskTransactionDetails', true),
    }),
    getShortSellEQHoldingDetailsService: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        searchClient?: string;
        searchSymbol?: string;
        source?: string;
        sortColumn?: string;
        sortDirection?: 'ASC' | 'DESC';
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        searchClient = '',
        searchSymbol = '',
        source = 'ap',
        sortColumn,
        sortDirection,
      }) => ({
        url: `live-risk/short-sell-eq-holding-details`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          searchClient,
          searchSymbol,
          source,
          sortColumn,
          sortDirection,
        },
      }),
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch EQ risk transaction details');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskEQRiskTransactionDetails', true),
    }),
    getClientWiseSegmentSummary: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        searchClient?: string;
        sortColumn?: string;
        sortDirection?: 'ASC' | 'DESC';
        source?: string;
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        searchClient = '',
        sortColumn,
        sortDirection,
        source = 'ap',
      }) => ({
        url: `live-risk/client-wise-segment-summary`,
        method: 'GET',
        params: { pageNumber, pageSize, searchClient, sortColumn, sortDirection, source },
      }),
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch client-wise segment summary');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskClientWiseSegmentSummary', true),
    }),
    getCompanyCodeWiseSummaryService: builder.query<any, { source?: string }>({
      query: ({ source = 'ap' }) => ({
        url: `live-risk/company-code-wise-summary`,
        method: 'GET',
        params: { source },
      }),
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch company code-wise summary');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskCompanyCodeWiseSummary', true),
    }),
    getShortfallData: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        source?: string;
        searchClient?: string;
        sortColumn?: string;
        searchDays?: string;
        sortDirection?: 'ASC' | 'DESC';
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        source = 'ap',
        searchClient = '',
        sortColumn,
        sortDirection,
        searchDays,
      }) => {
        // whitelist allowed sort columns for shortfall
        const allowed = [
          'client_id',
          'client_name',
          'branch_code',
          'exchange',
          'days',
          'excess_shortfall_max',
          'penlty_per',
          'penlty_charge_to_client',
          'penlty_notcharge_to_client_peak_penlty',
          'shortfall_amount',
          'shortfall_date',
          'status',
        ];
        const safeSort = allowed.includes(String(sortColumn)) ? sortColumn : undefined;
        return {
          url: `live-risk/shortfall-data`,
          method: 'GET',
          params: {
            pageNumber,
            pageSize,
            source,
            searchClient,
            searchDays,
            sortColumn: safeSort,
            sortDirection,
          },
        };
      },
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch shortfall data');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskShortfallData', true),
    }),
    getTplusFourData: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        source?: string;
        searchClient?: string;
        sortColumn?: string;
        sortDirection?: 'ASC' | 'DESC';
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        source = 'ap',
        searchClient = '',
        sortColumn,
        sortDirection,
      }) => {
        const allowed = [
          'client_id',
          'client_name',
          'branch',
          'code',
          'scrip_name',
          'isin',
          'setl',
          'balance',
          'qty',
          'rate',
          'amount',
          'sell',
          'sell_amt',
          'percentage',
          'financial_year',
          't4_amount',
          'status',
        ];
        const safeSort = allowed.includes(String(sortColumn)) ? sortColumn : undefined;
        return {
          url: `live-risk/t4-data`,
          method: 'GET',
          params: {
            pageNumber,
            pageSize,
            source,
            searchClient,
            sortColumn: safeSort,
            sortDirection,
          },
        };
      },
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch Tplusfour data');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskTplusFourData', true),
    }),
    getTplusFiveData: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        source?: string;
        searchClient?: string;
        sortColumn?: string;
        sortDirection?: 'ASC' | 'DESC';
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        source = 'ap',
        searchClient = '',
        sortColumn,
        sortDirection,
      }) => {
        const allowed = [
          'client_id',
          'client_name',
          'branch',
          'code',
          'scrip_name',
          'isin',
          'setl',
          'balance',
          'qty',
          'rate',
          'amount',
          'sell',
          'sell_amt',
          'percentage',
          'financial_year',
          't5_amount',
          'status',
        ];
        const safeSort = allowed.includes(String(sortColumn)) ? sortColumn : undefined;
        return {
          url: `live-risk/t5-data`,
          method: 'GET',
          params: {
            pageNumber,
            pageSize,
            source,
            searchClient,
            sortColumn: safeSort,
            sortDirection,
          },
        };
      },
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch T5 data');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskT5Data', true),
    }),
    getMtfMarginData: builder.query<
      any,
      {
        pageNumber?: number;
        pageSize?: number;
        source?: string;
        searchClient?: string;
        sortColumn?: string;
        sortDirection?: 'ASC' | 'DESC';
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        source = 'ap',
        searchClient = '',
        sortColumn,
        sortDirection,
      }) => {
        const allowed = [
          'trade_date',
          'client_id',
          'client_code',
          'client_name',
          'mtf_cash_collatral',
          'mtf_share_collatral',
          'total_margin',
          'mtm_loss',
          'margin_short_acess',
          'margin_amount',
          'margin_date',
          'status',
        ];
        const safeSort = allowed.includes(String(sortColumn)) ? sortColumn : undefined;
        return {
          url: `live-risk/mtf-margin-data`,
          method: 'GET',
          params: {
            pageNumber,
            pageSize,
            source,
            searchClient,
            sortColumn: safeSort,
            sortDirection,
          },
        };
      },
      transformResponse: (response: any) => {
        if (response?.statusCode >= 400)
          throw new Error(response.message || 'Failed to fetch MTF margin data');
        return response.data ?? response;
      },
      transformErrorResponse: createApiErrorTransformer('LiveRiskMtfMarginData', true),
    }),
  }),
});

export const {
  useGetClientWiseEqQuery,
  useGetClientWiseSegmentSummaryQuery,
  useGetScriptWiseEqQuery,
  useGetScriptWiseFnoQuery,
  useGetClientWiseFnoQuery,
  useGetFNORiskTransactionDetailsServiceQuery,
  useGetEQRiskTransactionDetailsServiceQuery,
  useGetShortSellEQHoldingDetailsServiceQuery,
  useGetCompanyCodeWiseSummaryServiceQuery,
  useGetShortfallDataQuery,
  useGetTplusFourDataQuery,
  useGetTplusFiveDataQuery,
  useGetMtfMarginDataQuery,
} = liveRiskApi;

// ─── Live Risk Excel Export ───────────────────────────────────────────────────

export type LiveRiskExportType = 't4' | 't5' | 'mtf' | 'shortfall';

export const LIVE_RISK_EXPORT_CONFIG: Record<
  LiveRiskExportType,
  { endpoint: string; defaultFileName: string; label: string }
> = {
  t4: {
    endpoint: 'live-risk/t4-data',
    defaultFileName: 't4-data.xlsx',
    label: 'T+4 Data',
  },
  t5: {
    endpoint: 'live-risk/t5-data',
    defaultFileName: 't5-data.xlsx',
    label: 'T+5 Data',
  },
  mtf: {
    endpoint: 'live-risk/mtf-margin-data',
    defaultFileName: 'mtf-margin-data.xlsx',
    label: 'MTF Margin Report',
  },
  shortfall: {
    endpoint: 'live-risk/shortfall-data',
    defaultFileName: 'shortfall-data.xlsx',
    label: 'Shortfall Data',
  },
};

/**
 * Safely decodes base64 string (handling newlines, whitespace, url-safe encoding, padding) into Uint8Array.
 */
export const robustBase64ToUint8Array = (input: string): Uint8Array<ArrayBuffer> => {
  let cleaned = String(input || '')
    .replace(/^data:[^;]+;base64,/, '')
    .replace(/\s+/g, '')
    .trim();

  // Handle URL-encoded base64 strings if present
  if (cleaned.includes('%')) {
    try {
      cleaned = decodeURIComponent(cleaned);
    } catch {
      // ignore
    }
  }

  // Convert URL-safe base64 to standard base64
  cleaned = cleaned.replace(/-/g, '+').replace(/_/g, '/');

  // Fix padding if missing
  const mod4 = cleaned.length % 4;
  if (mod4 === 2) {
    cleaned += '==';
  } else if (mod4 === 3) {
    cleaned += '=';
  } else if (mod4 === 1) {
    cleaned += '===';
  }

  try {
    const binaryString = window.atob(cleaned);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch {
    // Custom robust base64 decode fallback
    const b64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
    const lookup = new Int16Array(256);
    lookup.fill(-1);
    for (let i = 0; i < b64Chars.length; i++) {
      lookup[b64Chars.charCodeAt(i)] = i;
    }

    const unpadded = cleaned.replace(/=+$/, '');
    const outLength = Math.floor((unpadded.length * 3) / 4);
    const bytes = new Uint8Array(outLength);
    let p = 0;

    for (let i = 0; i < unpadded.length; i += 4) {
      const c0 = lookup[unpadded.charCodeAt(i)];
      const c1 = i + 1 < unpadded.length ? lookup[unpadded.charCodeAt(i + 1)] : -1;
      const c2 = i + 2 < unpadded.length ? lookup[unpadded.charCodeAt(i + 2)] : -1;
      const c3 = i + 3 < unpadded.length ? lookup[unpadded.charCodeAt(i + 3)] : -1;

      if (c0 === -1 || c1 === -1) break;

      bytes[p++] = (c0 << 2) | (c1 >> 4);
      if (c2 !== -1) {
        bytes[p++] = ((c1 & 15) << 4) | (c2 >> 2);
        if (c3 !== -1) {
          bytes[p++] = ((c2 & 3) << 6) | c3;
        }
      }
    }

    return bytes.subarray(0, p);
  }
};

/**
 * Downloads Live Risk Excel report as an .xlsx file using backend base64 payload.
 *
 * @param type - Export target ('t4' | 't5' | 'mtf' | 'shortfall')
 * @param customFileName - Optional custom filename override
 * @param source - API source identifier ('ap' or 'erpv2', default 'ap')
 */
export const downloadLiveRiskExcel = async (
  type: LiveRiskExportType,
  customFileName?: string,
  source: string = 'ap',
) => {
  const config = LIVE_RISK_EXPORT_CONFIG[type];
  if (!config) throw new Error(`Invalid export type: ${type}`);

  const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken') || '';

  const rawBase = import.meta.env.VITE_LIVE_RISK_URL || '';
  const baseUrl = rawBase.replace(/\/+$/, '');
  const url = `${baseUrl}/${config.endpoint.replace(/^\/+/, '')}?source=${encodeURIComponent(source)}&is_export=1`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { authToken: token, Authorization: `Bearer ${token}` } : {}),
    },
  });

  const contentType = response.headers.get('content-type') || '';
  let blob: Blob;
  let successMessage = `${config.label} generated`;

  // Handle direct file stream if returned
  if (
    contentType.includes('application/vnd.openxmlformats-officedocument') ||
    contentType.includes('application/octet-stream')
  ) {
    blob = await response.blob();
  } else {
    const result = await response.json();

    if (
      !response.ok ||
      result.success === false ||
      (!result.data && !result.file && !result.base64)
    ) {
      throw new Error(result.message || `Unable to generate ${config.label} Excel file`);
    }

    if (result.message) {
      successMessage = result.message;
    }

    let rawBase64 = '';
    if (typeof result.data === 'string') {
      rawBase64 = result.data;
    } else if (result.data && typeof result.data === 'object') {
      rawBase64 =
        result.data.data ||
        result.data.file ||
        result.data.base64 ||
        result.data.excel ||
        result.data.content ||
        '';
    } else if (typeof result.file === 'string') {
      rawBase64 = result.file;
    } else if (typeof result.base64 === 'string') {
      rawBase64 = result.base64;
    }

    if (!rawBase64) {
      throw new Error('Received empty Excel file data from server');
    }

    const bytes = robustBase64ToUint8Array(rawBase64);
    blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = customFileName || config.defaultFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);

  return { success: true, message: successMessage };
};
